/**
 * @fileoverview Error Boundary component for graceful error handling. Catches
 * React component errors and displays a fallback UI with recovery options.
 */

import * as Sentry from '@sentry/react';
import { Component, ReactNode } from 'react';
import { Alert, Box, Container, Typography } from '@mui/material';
import { ErrorOutlined, Home, Refresh } from '@mui/icons-material';
import { Button } from '@/shared/components/Button';
import { errorDetailsSx } from '../utils/errorHelpers';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional reset callback
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container maxWidth='md' sx={{ py: 8 }}>
          <Alert
            severity='error'
            icon={<ErrorOutlined fontSize="large" />}
            sx={{
              alignItems: 'center',
              '& .MuiAlert-icon': {
                fontSize: '3rem',
              },
            }}
          >
            <Typography variant='h6' gutterBottom>
              Something went wrong
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                mb: 2
              }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </Typography>

            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <Box component='pre' sx={{ ...errorDetailsSx, maxHeight: 200 }}>
                {this.state.errorInfo.componentStack}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant='contained'
                startIcon={<Refresh />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant='outlined'
                startIcon={<Home />}
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </Box>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

